import { CCInteger, Component, instantiate, Node, NodePool, Prefab, _decorator } from 'cc';

import { DEV } from 'cc/env';
import NodePoolInstance from '../../CCClass/NodePoolInstance';

const { ccclass, property } = _decorator;

enum EventType {
    ITEM_GENERATED = 'ITEM_GENERATED',
}

@ccclass('ItemGenerator')
export default class ItemGenerator extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property(Node)
    private _itemRoot: Node = null;

    @property({ type: Prefab, formerlySerializedAs: 'itemPrefab' })
    private _itemPrefab: Prefab = null;

    @property({ tooltip: DEV && '新增Item是否從最前面插入' })
    private readonly insertChildAtFront = false;

    @property({ tooltip: DEV && '移除Item是否從最前面移除' })
    private readonly removeChildAtFront = false;

    @property
    private readonly useNodePoolInstance: boolean = false;

    private readonly _componentMap: Map<typeof Component, Map<Node, Component>> = new Map();
    private _nodePoolInstance: NodePoolInstance = null;
    private _nodePool: NodePool = null;
    private _displayItemCount = 0;

    @property({ type: Node, displayOrder: 0 })
    public get itemRoot() {
        return this._itemRoot ?? this.node;
    }
    public set itemRoot(value: Node) {
        this._itemRoot = value;
    }

    public get items() {
        return this.itemRoot.children;
    }

    @property({ type: CCInteger })
    public get displayItemCount() {
        return this._displayItemCount;
    }
    public set displayItemCount(value: number) {
        if (value < 0) {
            throw new Error('itemCount must be greater than or equal to 0.');
        }
        if (value === this._displayItemCount) {
            return;
        }
        this._displayItemCount = value;
        this._updateContent();
    }

    @property({ type: Prefab })
    public get itemPrefab() {
        return this._itemPrefab;
    }
    public set itemPrefab(value: Prefab) {
        if (this._itemPrefab === value) {
            return;
        }
        this._itemPrefab = value;
    }

    private get nodePoolInstance() {
        if (!this._nodePoolInstance) {
            this._nodePoolInstance = new NodePoolInstance();
            this._nodePoolInstance.prefab = this.itemPrefab;
            this._nodePoolInstance.init((node: Node) => {
                this.node.emit(EventType.ITEM_GENERATED, node);
            });
        }
        return this._nodePoolInstance;
    }

    public prespawnItems(count: number) {
        if (!this.useNodePoolInstance) {
            throw new Error('prespawnItems is only available when use NodePoolInstance.');
        }
        return this.nodePoolInstance.prespawn(count);
    }

    public getItemsComponent<T extends Component>(componentType: abstract new () => T): T[] {
        let componentMap: Map<Node, T> = this._componentMap.get(componentType as any) as Map<Node, T>;
        if (!componentMap) {
            componentMap = new Map<Node, T>();
            this._componentMap.set(componentType as any, componentMap);
        }
        return this.items.map((item) => {
            let component = componentMap.get(item);
            if (!component) {
                component = item.getComponent(componentType as any);
                componentMap.set(item, component);
            }
            return component;
        });
    }

    public getItemComponent<T extends Component>(index: number, componentType: abstract new () => T): T {
        if (index < 0 || index >= this.items.length) {
            throw new Error('Index out of range.');
        }
        let componentMap: Map<Node, T> = this._componentMap.get(componentType as any) as Map<Node, T>;
        if (!componentMap) {
            componentMap = new Map<Node, T>();
            this._componentMap.set(componentType as any, componentMap);
        }
        const item = this.items[index];
        let component = componentMap.get(item);
        if (!component) {
            component = item.getComponent(componentType as any);
            componentMap.set(item, component);
        }
        return component;
    }

    private _updateContent() {
        const currentItemCount = this.itemRoot.children.length;
        if (this.displayItemCount > currentItemCount) {
            for (let i = 0; i < this.displayItemCount - currentItemCount; i++) {
                const newNode = this._getItem();
                this.itemRoot.addChild(newNode);
                newNode.setSiblingIndex(this.insertChildAtFront ? 0 : this.itemRoot.children.length);
            }
        } else if (currentItemCount > this.displayItemCount) {
            for (let i = this.displayItemCount; i < currentItemCount; i++) {
                const removedNode = this.itemRoot.children[this.removeChildAtFront ? 0 : this.itemRoot.children.length - 1];
                this._putItem(removedNode);
            }
        }
    }

    private _getItem(): Node {
        if (this.useNodePoolInstance) {
            return this.nodePoolInstance.get();
        }
        if (!this._nodePool) {
            this._nodePool = new NodePool();
        }
        return (
            this._nodePool.get() ??
            (() => {
                const node = instantiate(this.itemPrefab);
                this.node.emit(EventType.ITEM_GENERATED, node);
                return node;
            })()
        );
    }

    private _putItem(node: Node) {
        if (this.useNodePoolInstance) {
            this.nodePoolInstance.put(node);
        } else {
            this._nodePool.put(node);
        }
    }
}
